import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';

interface PageTransitionProps {
  children: React.ReactNode;
  transitionType?: 'fade' | 'slide' | 'scale';
  duration?: number;
  className?: string;
}

const PageTransition: React.FC<PageTransitionProps> = ({
  children,
  transitionType = 'fade',
  duration = 400,
  className = '',
}) => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState<'idle' | 'exiting' | 'entering'>('idle');
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (location !== displayLocation) {
      // Start exit transition
      setTransitionStage('exiting');
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        // Update location and start enter transition
        setDisplayLocation(location);
        setTransitionStage('entering');
        
        // Safely scroll to top if container exists
        if (containerRef.current) {
          try {
            containerRef.current.scrollTop = 0;
          } catch (error) {
            // Fallback to window scroll if container scroll fails
            try {
              window.scrollTo(0, 0);
            } catch (windowError) {
              // Silent fail if both methods fail
              console.warn('Could not scroll to top during page transition');
            }
          }
        }
        
        timeoutRef.current = setTimeout(() => {
          setTransitionStage('idle');
        }, duration);
      }, duration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [location, displayLocation, duration]);

  const getTransitionStyles = () => {
    const baseStyles = {
      transition: `all ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
    };

    switch (transitionType) {
      case 'slide':
        return {
          ...baseStyles,
          transform: transitionStage === 'exiting' 
            ? 'translateX(-100%)' 
            : transitionStage === 'entering'
            ? 'translateX(100%)'
            : 'translateX(0)',
          opacity: transitionStage === 'idle' ? 1 : 0.8,
        };
      
      case 'scale':
        return {
          ...baseStyles,
          transform: transitionStage === 'exiting' || transitionStage === 'entering'
            ? 'scale(0.95)'
            : 'scale(1)',
          opacity: transitionStage === 'idle' ? 1 : 0,
        };
      
      default: // fade
        return {
          ...baseStyles,
          opacity: transitionStage === 'idle' ? 1 : 0,
        };
    }
  };

  return (
    <Box
      ref={containerRef}
      className={`page-transition ${className}`}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        ...getTransitionStyles(),
      }}
      data-transition-stage={transitionStage}
      data-transition-type={transitionType}
    >
      {React.cloneElement(children as React.ReactElement, {
        key: displayLocation.pathname,
      })}
    </Box>
  );
};

// Alternative simpler transition component for cases where the main one causes issues
export const SimplePageTransition: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    setIsVisible(false);
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <Box
      className={`simple-page-transition ${className}`}
      sx={{
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 200ms ease-in-out',
        width: '100%',
        height: '100%',
      }}
    >
      {children}
    </Box>
  );
};

// Route transition wrapper that can be used with React Router
export const RouteTransition: React.FC<{
  children: React.ReactNode;
  transitionType?: 'fade' | 'slide' | 'scale';
  duration?: number;
}> = ({ children, transitionType = 'fade', duration = 300 }) => {
  const location = useLocation();
  
  return (
    <Box
      key={location.pathname}
      className="route-transition"
      sx={{
        animation: 'fadeInUp 0.3s ease-out',
        width: '100%',
        height: '100%',
      }}
    >
      {children}
    </Box>
  );
};

export default PageTransition;